import React, {useEffect, useState} from 'react';
import PropTypes from "prop-types";
import { makeStyles, useTheme, Paper, Typography } from '@material-ui/core';
import arrowDown from "../../../images/downArrow.png";
import arrowUp from '../../../images/upArrow.png';
import CountUp from 'react-countup';
import {themeTypeToBool} from '../../../utils/helpers';
import PeriodSelect from './PeriodSelect';
import { TooltipRight } from "../../Common/CustomTooltips";
import t from "../../../languages/pl";

const useStyles = makeStyles(theme => ({
    container: {
        minWidth: 350,
        height: 130,
        margin: theme.spacing(1),
        background: theme.palette.background.paper,
        position: "relative"
    },
    title: {
        padding: 15,
        paddingBottom: 5
    },
    content: {
        paddingLeft: theme.spacing(2),
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-start"
    },
    mainInfo: {
        fontWeight: 600,
        color: themeTypeToBool(theme.palette.type) ? "#6075DD" : "#FFD1A4"
    },
    barChart: {
        width: "30%",
        position: "absolute",
        right: 15,
        bottom: 10
    },
    balanceDiv: {
        paddingTop: 10,
        paddingLeft: 15,
        height: "40%"
    },
    arrowImg: {
        height: "0.9em"
    },
    balanceValue: {
        fontWeight: 500,
        width: 70,
        cursor: "pointer"
    },
    settingsIcon: {
        position: "absolute",
        top: 10,
        right: 10,
        padding: 0,
    }
}));

const periodOptions = [
    {
        name: t.week,
        value: 1
    },
    {
        name: t.month,
        value: 2
    },
    {
        name: t.quarter,
        value: 3
    }
];

function CountUpValue(props) {
    const theme = useTheme();
    const classes = useStyles(theme);
    const {decimals, units, barChartImg, data,
        averageMileage, averageDriveTime, combustionForFleet} = props;
    const [valueToDisplay, setValue] = useState(1);
    const [balance, setBalance]= useState("");
    const [period, setPeriod] = useState(0);
    const [title, setTitle] = useState("");
    const [startValue, setStartValue] = useState(0);

    useEffect(() => {
        setPeriod(1);
        if (averageMileage) {
                setValue(data.tydzien.przebieg);
                setBalance(data.tydzien.przebieg_procent);
                setTitle(t.average2 + " " + t.weekly + " " + t.fleetMileage);
        }
        else if (averageDriveTime){
            setValue(data.tydzien.czas);
            setBalance(data.tydzien.czas_procent);
            setTitle(t.average2 + " " + t.weekly + " " + t.driveTime);
        }
        else if(combustionForFleet) {
            setValue(data.tydzien.spalanie);
            setBalance(data.tydzien.spalanie_procent);
            setTitle(t.average3 + " " + t.weekly1 + " " + t.combustionForFleet);
        }

    }, [data]);

    const cutBalanceUnit = (data) => {
        if(data != null)
            return parseInt(data.slice(0, data.length-1));
    };

    const handlePeriodChange = (value) => {
        setStartValue(valueToDisplay);
        let periodName ="";
        if (averageMileage) {
            if (value === 1) {
                setPeriod(1);
                setValue(data.tydzien.przebieg);
                setBalance(data.tydzien.przebieg_procent);
                periodName = t.weekly;
            }
            if (value === 2) {
                setPeriod(2);
                setValue(data.miesiac.przebieg);
                setBalance(data.miesiac.przebieg_procent);
                periodName = t.monthly;
            }
            if (value === 3) {
                setPeriod(3);
                setValue(data.kwartal.przebieg);
                setBalance(data.kwartal.przebieg_procent);
                periodName = t.quarterly;
            }
            setTitle(t.average2 + " " + periodName + " " + t.fleetMileage);
        }
        else if (averageDriveTime) {
            if (value === 1) {
                setPeriod(1);
                setValue(data.tydzien.czas);
                setBalance(data.tydzien.czas_procent);
                periodName = t.weekly;
            }
            if (value === 2) {
                setPeriod(2);
                setValue(data.miesiac.czas);
                setBalance(data.miesiac.czas_procent);
                periodName = t.monthly;
            }
            if (value === 3) {
                setPeriod(3);
                setValue(data.kwartal.czas);
                setBalance(data.kwartal.czas_procent);
                periodName = t.quarterly;
            }
            setTitle(t.average2 + " " + periodName + " " + t.driveTime);
        }
        else if (combustionForFleet) {
            if (value === 1) {
                setPeriod(1);
                setValue(data.tydzien.spalanie);
                setBalance(data.tydzien.spalanie_procent);
                periodName = t.weekly1;
            }
            if (value === 2) {
                setPeriod(2);
                setValue(data.miesiac.spalanie);
                setBalance(data.miesiac.spalanie_procent);
                periodName = t.monthly1;
            }
            if (value === 3) {
                setPeriod(3);
                setValue(data.kwartal.spalanie);
                setBalance(data.kwartal.spalanie_procent);
                periodName = t.quarterly1;
            }
            setTitle(t.average3 + " " + periodName + " " + t.combustionForFleet);
        }
    };

    return (
        <Paper className = {classes.container} square>
            <Typography variant="subtitle2" className={classes.title}>
                 {title}
            </Typography>
            <div className={classes.content}>
                <Typography variant="h4" className={classes.mainInfo}>
                    <CountUp
                        start={startValue}
                        end={valueToDisplay}
                        duration={5}
                        decimals={decimals}
                        decimal=","
                    />
                </Typography>
                &nbsp;
                <Typography variant="h5" className={classes.mainInfo} style={{paddingBottom: 2}}>
                    {units}
                </Typography>
            </div>
            <div className={classes.balanceDiv}>
                <img
                    src={barChartImg}
                    className={classes.barChart}
                    alt="barChartImg"
                />
                <TooltipRight title={t.deviationFromPreviousPeriod}>
                    <Typography variant="subtitle2"
                            className={classes.balanceValue}
                            style={{color: (cutBalanceUnit(balance) > 0) ? "#3CC480" : "#FF6161" }}
                    >
                        <img
                            src={cutBalanceUnit(balance) > 0 ? arrowUp : arrowDown}
                            className={classes.arrowImg}
                            alt="arrowImg"
                        />
                            &nbsp;
                        {Math.abs(cutBalanceUnit(balance))}%
                    </Typography>
                </TooltipRight>
            </div>
            <PeriodSelect
                className={classes.settingsIcon}
                value={period}
                onChange={handlePeriodChange}
                buttonOptions={periodOptions}
            />
        </Paper>
    );
}

export default CountUpValue;

CountUpValue.propTypes = {
    data: PropTypes.object,
    decimals: PropTypes.number,
    units: PropTypes.string,
    barChartImg: PropTypes.node,
    averageMileage: PropTypes.bool,
    averageDriveTime: PropTypes.bool,
    combustionForFleet: PropTypes.bool,
};